/**
 * useInstallMarketplaceApp Hook
 *
 * React Query mutation hook for installing marketplace applications.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { tenantMarketplaceService } from '../../../api/marketplace.service';
import type { MarketplaceInstallDTO } from '../../../types/marketplace.types';
import type { ApplicationSanitized } from '../../../types/application.types';
import { getErrorMessage } from '../../../lib/utils/errors';

interface InstallAppParams {
  tenantId: string;
  data: MarketplaceInstallDTO;
}

/**
 * Hook for installing a marketplace application
 */
export const useInstallMarketplaceApp = (): UseMutationResult<
  ApplicationSanitized,
  Error,
  InstallAppParams
> => {
  const queryClient = useQueryClient();

  return useMutation<ApplicationSanitized, Error, InstallAppParams>({
    mutationFn: async ({ tenantId, data }) => {
      console.log('[useInstallMarketplaceApp] Installing app:', data.listingId);
      return await tenantMarketplaceService.installApp(tenantId, data);
    },
    onSuccess: (_, { tenantId }) => {
      // Invalidate applications list
      queryClient.invalidateQueries({ queryKey: ['applications', tenantId] });
      // Invalidate installed marketplace apps
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'installed', tenantId] });
      // Invalidate marketplace listing (to update install count)
      queryClient.invalidateQueries({ queryKey: ['marketplace', 'listings'] });
    },
    onError: (error) => {
      console.error('[useInstallMarketplaceApp] Install failed:', getErrorMessage(error));
    },
  });
};
